import { Input, Flex as WebFlex } from '@aloft/toolkit-sui';

import { useState } from 'react';

import { PDFOnly } from '../../../summary/components/PDFOnly.tsx';
import { Typography } from '../../../summary/components/Typography.tsx';
import { WebOnly } from '../../../summary/components/WebOnly.tsx';
import { SummarySection } from '../SummarySection.tsx';
import { SummaryTableHorizontal } from '../SummaryTableHorizontal.js';
import { TitleAndExportToggleLockup } from '../TitleAndExportToggleLockup.tsx';
import { Tkv2PairedCostSummaryInterfaceV2 } from '../types.ts';
import { adjustment } from '../utils/formatters/adjustment.ts';
import { calculations } from '../utils/formatters/calculations.ts';
import { PairedCostSettingsBreakdown } from './PairedCostSettingsBreakdown.tsx';
import { isCosted } from './isCosted.ts';

export const AdjustmentBreakdownBelowGrade = (props: {
  summary: Tkv2PairedCostSummaryInterfaceV2;
  compSequence: string[];
  id: string;
  title: string;
}) => {
  const { summary } = props;
  const commentaryProps = summary.exportableCommentaries.belowGradeANSI;
  const [commentary, setCommentary] = useState(commentaryProps.commentary ?? '');

  if (!isCosted(summary.results, 'belowGradeANSI')) {
    return null;
  }
  return (
    <SummarySection>
      <TitleAndExportToggleLockup {...props} commentary={commentaryProps} />
      <Typography variant="body1">
        Below grade (per ANSI) square footage, also referred to as non-standard GLA, is square
        footage that is below grade per the ANSI measurement standard but considered to have a cost
        to build that is similar to typical above grade square footage. This adjustment is
        calculated by subtracting the comparable's below grade per ANSI value from the subject's
        below grade per ANSI value.
      </Typography>
      <PDFOnly>
        <Typography variant="body1">{commentaryProps.commentary ?? ''}</Typography>
      </PDFOnly>
      <SummaryTableHorizontal
        {...props}
        columns={[
          {
            header: 'Square Feet',
            type: 'feature',
            path: 'text',
            adjustmentType: 'belowGradeANSI',
          },
          {
            header: 'Adjustment Calculation',
            type: 'adjustment',
            path: 'calculations',
            valueFormatter: calculations,
            adjustmentType: 'belowGradeANSI',
          },
          {
            header: 'Adjustment',
            type: 'adjustment',
            path: 'adjustment',
            valueFormatter: adjustment,
            textAlign: 'right',
            adjustmentType: 'belowGradeANSI',
            includeTooltip: true,
          },
        ]}
      />
      <PairedCostSettingsBreakdown {...props.summary} type="belowGradeANSI" />
      <WebOnly>
        <WebFlex marginTop="1rem" width="40rem">
          <Input
            type="multiline"
            onChange={setCommentary}
            value={commentary}
            onClear={() => setCommentary('')}
            onBlur={() => commentaryProps.updateCommentary(commentary)}
            resize="both"
            label="Commentary"
            size="sm"
          />
        </WebFlex>
      </WebOnly>
    </SummarySection>
  );
};
